/*
 * Which free-event banner state to draw (design W2, look 2A), from the user
 * payload's `free_event` alone (Req 16). First match wins, in the sheet's
 * order; null means no banner.
 *
 * Fields beyond rails openapi `FreeEvent` (PR #1447) are ASSUMED from rails
 * PR B and listed in the react PR body: paid_at, guest_data_deleted_at,
 * unused_expired_at, gallery_offline_notice_at, unused_warning_at,
 * restore_only, event_time_zone, channel_id, channel_name.
 */
const passed = (iso, now) => Boolean(iso) && new Date(iso).getTime() <= now.getTime();

export const bannerState = (fe, now = new Date()) => {
  if (!fe || !fe.state) return null;
  if (fe.paid_at) return null;
  if (fe.guest_data_deleted_at) return "gone";
  if (fe.state === "expired") {
    if (fe.unused_expired_at || !fe.window_starts_at) return "unused";
    return passed(fe.gallery_restorable_until, now) ? "gone" : "off";
  }
  if (fe.state === "retained") {
    return passed(fe.gallery_offline_notice_at, now) ? "soon" : "ret";
  }
  if (fe.state === "live") {
    if (fe.sms_cap_reached_at) return "cap";
    if (typeof fe.sms_warn_at === "number" && (fe.sms_sent_count || 0) >= fe.sms_warn_at) return "warn";
    return "live";
  }
  if (fe.state === "available") {
    if (fe.refusal === "device_used") return "ref";
    if (fe.refusal === "unverified") return "unv";
    if (fe.refusal === "email_used") return "eu";
    if (passed(fe.unused_warning_at, now)) return fe.channel_id ? "useby" : "useby0";
    return fe.channel_id ? "avail" : "avail0";
  }
  return null;
};

/*
 * The free-event phase that picks the plan sheet's tense (Morty's words r2):
 * upcoming → pl.flag_upcoming and no pl.sub; retained / restorable →
 * pl.sub_<phase> and pl.flag_after; null → neither line.
 */
export const freeEventPhase = (fe, now = new Date()) => {
  if (!fe || !fe.state || fe.paid_at) return null;
  if ((fe.state === "available" || fe.state === "live") && !fe.refusal) return "upcoming";
  if (fe.state === "retained") return "retained";
  if (
    fe.state === "expired" &&
    fe.gallery_restorable_until &&
    !passed(fe.gallery_restorable_until, now) &&
    !fe.guest_data_deleted_at &&
    !fe.unused_expired_at &&
    fe.window_starts_at
  ) {
    return "restorable";
  }
  return null;
};

/*
 * Per state: stub class, stub label key, title key, body key, action set.
 * Mirrors the sheet's FB table (tools/build.py).
 */
export const BANNER = {
  avail: { cls: "s-avail", stub: "stub.free", b: "fb.av.b", p: "fb.av", act: "avail" },
  avail0: { cls: "s-avail", stub: "stub.free", b: "fb.av.b", p: "fb.av", act: "none" },
  useby: { cls: "s-avail", stub: "stub.free", b: "fb.use.b", p: "fb.av", act: "avail" },
  useby0: { cls: "s-avail", stub: "stub.free", b: "fb.use.b", p: "fb.av", act: "none" },
  live: { cls: "s-live", stub: "stub.live", b: "fb.live.b", p: "fb.live", act: "live" },
  warn: { cls: "s-warn", stub: "stub.texts", b: "fb.live.b", p: "fb.warn", act: "meter" },
  cap: { cls: "s-warn", stub: "stub.texts", b: "fb.live.b", p: "fb.cap", act: "meter" },
  ret: { cls: "s-ret", stub: "stub.after", b: "fb.ret.b", p: "fb.ret", act: "ret" },
  soon: { cls: "s-soon", stub: "stub.soon", b: "fb.soon.b", p: "fb.soon", act: "ret" },
  off: { cls: "s-off", stub: "stub.off", b: "fb.off.b", p: "fb.off", act: "plans" },
  gone: { cls: "s-gone", stub: "stub.end", b: "fb.gone.b", p: "fb.gone", act: "plans" },
  ref: { cls: "s-ref", stub: "stub.note", b: "fb.ref.b", p: "fb.ref", act: "plans" },
  unused: { cls: "s-unused", stub: "stub.exp", b: "fb.un.b", p: "fb.un", act: "plans" },
  unv: { cls: "s-ref", stub: "stub.note", b: "fb.unv.b", p: "fb.unv", act: "unv" },
  eu: { cls: "s-ref", stub: "stub.note", b: "fb.eu.b", p: "fb.eu", act: "plans" },
};
